import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'

/** Events inside [from, to] (ISO date strings) — GET /calendar/events. */
export function useCalendarEvents(from: string, to: string) {
  return useQuery({
    queryKey: ['calendar-events', from, to],
    queryFn: () => api.calendarEvents(from, to),
    enabled: !!from && !!to,
  })
}

/** Production-calendar day types inside [from, to] — GET /calendar/production-days. */
export function useProductionDays(from: string, to: string) {
  return useQuery({
    queryKey: ['calendar-production-days', from, to],
    queryFn: () => api.productionDays(from, to),
    enabled: !!from && !!to,
  })
}

// ─────────── Mail-import (Microsoft 365 / Graph, 🔶) ───────────

/** Статус подключения почтового календаря — GET /calendar/graph/status. */
export function useGraphStatus() {
  return useQuery({ queryKey: ['calendar-graph-status'], queryFn: api.graphStatus })
}

/** Получить URL согласия Entra (фронт открывает его во внешнем браузере). */
export function useGraphConnect() {
  return useMutation({ mutationFn: () => api.graphConnect() })
}

/** Кнопка «Обновить» — ручная инкрементальная синхронизация. */
export function useGraphSync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.graphSync(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-graph-status'] })
      qc.invalidateQueries({ queryKey: ['calendar-events'] })
    },
  })
}

/** Отключить почтовый календарь — удаляет импортированные GRAPH-события. */
export function useGraphDisconnect() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.graphDisconnect(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-graph-status'] })
      qc.invalidateQueries({ queryKey: ['calendar-events'] })
    },
  })
}

/** Private personnel form calendar; owner/workplace changes cannot reuse another calendar. */
export function useTimeOffProductionDays(from: string, to: string, employmentId?: string, ownerId?: string, enabled = true) {
  return useQuery({
    queryKey: ['edo-timeoff-production-days', from, to, ownerId ?? 'me', employmentId ?? null],
    queryFn: () => api.timeOffProductionDays(from, to, employmentId, ownerId),
    enabled: enabled && !!from && !!to,
  })
}
